/*
 * r16o3 v3-backlog (2026-10-24, the owner's future todo 10): what the sign-in page says when signing in fails.
 *
 * The bug. "Sign-in failed. Check your email and password." was shown whenever the failed request had ANY response. A server
 * that is down rarely produces "no response" in practice: in development the CRA proxy (src/setupProxy.js,
 * http-proxy-middleware) answers 504 with the text "Error occurred while trying to proxy ...", and in production nginx answers
 * 502 / 503 / 504 with an HTML page. None of those bodies carries an `error` field, so the page fell through to the
 * credentials sentence - for a server that never saw the password. That cost a real debugging session (2026-08-25).
 *
 * The rule here: only a 401 from the sign-in call itself means the email / password were refused (the server answers
 * { error: "invalid credentials" }, UiApiAuthController). No response, a gateway status (502 / 503 / 504) or a non-JSON
 * proxy error page means the server was not reached. A 401 from /me right after a successful sign-in means the password was
 * RIGHT but the browser did not keep the session cookie. Everything else is described by its status, with the server's own
 * words when it sent any.
 *
 * Switch: lib/r14Switch r16o3UiEnabled (necro.ui.r16o3). OFF = legacyLoginErrorMessage, the page's old wording exactly.
 */

const GATEWAY = new Set([502, 503, 504]);

function serverWords(data) {
  if (!data || typeof data !== 'object') return '';
  const w = data.error || data.detail || data.message;
  return typeof w === 'string' ? w.trim() : '';
}

function urlOf(error) {
  return String((error && error.config && error.config.url) || '');
}

/** A body that is a proxy's / gateway's own page (a string, or HTML), not the Spring server's JSON. */
function isProxyPage(data) {
  if (typeof data !== 'string') return false;
  const s = data.toLowerCase();
  return s.includes('proxy') || s.includes('<html') || s.includes('bad gateway') || s.includes('gateway time');
}

/**
 * @returns {{ kind: 'unreachable'|'credentials'|'session'|'refused'|'rate-limited'|'bad-request'|'server'|'other',
 *             status: number|null, message: string }}
 */
export function describeLoginError(error) {
  const res = error && error.response;
  if (!res) {
    const timeout = error && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT');
    return {
      kind: 'unreachable',
      status: null,
      message: timeout
        ? 'The server did not answer in time. Check your connection and try again.'
        : 'Could not reach the server. Check your connection, or whether the server is running.',
    };
  }
  const status = res.status;
  const words = serverWords(res.data);
  const url = urlOf(error);
  if (GATEWAY.has(status) || (status >= 500 && isProxyPage(res.data))) {
    return {
      kind: 'unreachable',
      status,
      message: `The server is not answering right now (HTTP ${status}). It may be restarting - try again in a minute. Your password was not checked.`,
    };
  }
  if (status === 401) {
    if (url.includes('/auth/login')) {
      return { kind: 'credentials', status, message: 'Sign-in failed. Check your email and password.' };
    }
    return {
      kind: 'session',
      status,
      message: 'Your email and password were accepted, but the browser did not keep the sign-in. Reload the page and try again; if it keeps happening, this browser may be blocking the site\'s cookies.',
    };
  }
  if (status === 403) {
    return {
      kind: 'refused',
      status,
      message: 'The server refused the sign-in request (HTTP 403), usually an expired page. Reload the page and try again.',
    };
  }
  if (status === 429) {
    return { kind: 'rate-limited', status, message: 'Too many sign-in attempts. Wait a minute, then try again.' };
  }
  if (status === 400) {
    return { kind: 'bad-request', status, message: words ? `Sign-in failed: ${words}.` : 'Sign-in failed: the server could not read the request (HTTP 400).' };
  }
  if (status >= 500) {
    return {
      kind: 'server',
      status,
      message: `The server had a problem signing you in (HTTP ${status})${words ? `: ${words}` : ''}. Try again; if it keeps happening, tell support.`,
    };
  }
  return { kind: 'other', status, message: words ? `Sign-in failed: ${words}` : `Sign-in failed (HTTP ${status}).` };
}

/** The page's wording before r16o3, kept verbatim for the switch's OFF position. */
export function legacyLoginErrorMessage(error) {
  if (!error.response) return 'Could not reach the server. Is the backend running?';
  return error.response.data?.error || error.response.data?.detail || 'Sign-in failed. Check your email and password.';
}

/** One console line per failed sign-in, so a support session can tell the cases apart from the console alone. */
export function logLoginError(error, d) {
  // eslint-disable-next-line no-console
  console.warn(`[LOGIN-ERROR] kind=${d.kind} status=${d.status === null ? 'none' : d.status} url=${urlOf(error) || '?'} code=${(error && error.code) || ''}`);
}
